import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import maplibregl from 'maplibre-gl';
import { Loader2, Navigation, Search, X } from 'lucide-react';

import { TILE_LAYERS } from '@/services/map-tiles';
import type { TileLayerKey } from '@/services/map-tiles';
import { MapStyleSwitcher } from '@/components/map/MapStyleSwitcher';
import { HuntMap } from '@/components/map/HuntMap';
import { geocodeAddress, type GeocodeResult } from '@/services/geocoding-service';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';

// Default to US center when no initialCoordinates given
const US_CENTER: [number, number] = [39, -98];
const US_CENTER_ZOOM = 4;
const PINNED_ZOOM = 15;
const DEBOUNCE_MS = 500;

export interface MapPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (coords: { lat: number; lng: number }) => void;
  initialCoordinates?: { lat: number; lng: number };
  initialStyle?: TileLayerKey;
  title?: string;
}

export function MapPickerModal({
  isOpen,
  onClose,
  onConfirm,
  initialCoordinates,
  initialStyle = 'satellite' as TileLayerKey,
  title = 'Pick Location',
}: MapPickerModalProps) {
  const mapRef = useRef<maplibregl.Map | null>(null);
  const mountedRef = useRef(true);
  const [activeStyle, setActiveStyle] = useState<TileLayerKey>(initialStyle);
  const [center, setCenter] = useState<{ lat: number; lng: number }>(
    initialCoordinates ?? { lat: US_CENTER[0], lng: US_CENTER[1] },
  );
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isGpsLocating, setIsGpsLocating] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showManual, setShowManual] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Track online status
  useEffect(() => {
    function handleOnline() { setIsOnline(true); }
    function handleOffline() { setIsOnline(false); }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Dismiss search results on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced geocode search
  useEffect(() => {
    if (!query.trim() || !isOnline) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- early-return branch clears state before async path
      setResults([]);
      setShowResults(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const items = await geocodeAddress(query);
        setResults(items);
        setShowResults(true);
      } catch {
        setResults([]);
        setShowResults(false);
      } finally {
        setIsSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, isOnline]);

  // Sync active style to map
  useEffect(() => {
    if (mapRef.current && mountedRef.current) {
      try { mapRef.current.setStyle(TILE_LAYERS[activeStyle].style); } catch { /* map may be destroyed */ }
    }
  }, [activeStyle]);

  // Lock body scroll while modal is open
  useBodyScrollLock(isOpen);

  function handleGpsClick() {
    if (!navigator.geolocation) return;
    setIsGpsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: gpsLat, longitude: gpsLng } = pos.coords;
        mapRef.current?.flyTo({ center: [gpsLng, gpsLat], zoom: PINNED_ZOOM, duration: 800 });
        setIsGpsLocating(false);
      },
      () => { setIsGpsLocating(false); },
      { timeout: 10000, enableHighAccuracy: true },
    );
  }

  const handleSelectResult = useCallback((result: GeocodeResult) => {
    setQuery(result.address.split(',')[0]);
    setShowResults(false);
    mapRef.current?.flyTo({
      center: [result.longitude, result.latitude],
      zoom: PINNED_ZOOM,
      duration: 800,
    });
  }, []);

  function handleConfirm() {
    onConfirm({ lat: center.lat, lng: center.lng });
  }

  const mapCenter: [number, number] = initialCoordinates
    ? [initialCoordinates.lat, initialCoordinates.lng]
    : US_CENTER;
  const mapZoom = initialCoordinates ? PINNED_ZOOM : US_CENTER_ZOOM;

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[var(--color-background)]"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 bg-bark-900 border-b border-bark-700 shrink-0 h-12">
        <button
          type="button"
          onClick={onClose}
          className="text-body-sm text-ember-400 hover:text-ember-300 transition-colors"
          aria-label="Cancel"
        >
          ← Cancel
        </button>
        <span className="text-body-sm font-semibold text-bark-100">{title}</span>
        {/* Spacer to balance the cancel button */}
        <div className="w-16" aria-hidden="true" />
      </div>

      {/* Map area — fills remaining height between top bar and bottom strip */}
      <div className="relative flex-1 min-h-0">
        {/* Search bar — top of map */}
        {isOnline && (
          <div
            ref={searchRef}
            className="absolute top-3 left-3 right-3 z-[1001]"
          >
            <div className="relative flex items-center">
              <Search
                size={16}
                className="absolute left-3 text-[var(--color-text-muted)] pointer-events-none"
                aria-hidden="true"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => results.length > 0 && setShowResults(true)}
                placeholder="Search for a town, address, or place…"
                aria-label="Search location"
                className="bg-[var(--color-surface)] rounded-md text-body-md text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] pl-9 pr-9 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-0 shadow-md"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); setResults([]); setShowResults(false); }}
                  className="absolute right-3 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {isSearching && (
              <p className="mt-1 text-body-xs text-[var(--color-text-muted)] pl-1">Searching…</p>
            )}

            {showResults && results.length > 0 && (
              <div
                className="mt-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md shadow-lg overflow-hidden"
                role="listbox"
              >
                {results.map((r, i) => (
                  <button
                    key={i}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="w-full text-left px-4 py-2.5 text-body-sm text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] transition-colors border-b border-[var(--color-border)] last:border-b-0"
                    onClick={() => handleSelectResult(r)}
                  >
                    {r.address}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Full-screen map */}
        <HuntMap
          center={mapCenter}
          zoom={mapZoom}
          className="h-full w-full"
          initialStyle={activeStyle}
          onMapReady={(map) => {
            mapRef.current = map;
            // Update coordinate readout in real time as map moves
            map.on('move', () => {
              const c = map.getCenter();
              setCenter({ lat: c.lat, lng: c.lng });
            });
          }}
        />

        {/* Crosshair — always centered, pointer-events: none */}
        <div
          data-testid="map-crosshair"
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ zIndex: 1000 }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ filter: 'drop-shadow(0 0 1.5px rgba(0,0,0,0.9))' }}
          >
            {/* Horizontal line */}
            <line x1="0" y1="20" x2="15" y2="20" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <line x1="25" y1="20" x2="40" y2="20" stroke="white" strokeWidth="2" strokeLinecap="round" />
            {/* Vertical line */}
            <line x1="20" y1="0" x2="20" y2="15" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <line x1="20" y1="25" x2="20" y2="40" stroke="white" strokeWidth="2" strokeLinecap="round" />
            {/* Center dot */}
            <circle cx="20" cy="20" r="2.5" fill="white" />
          </svg>
        </div>

        {/* GPS button — bottom-left of map */}
        <button
          type="button"
          onClick={handleGpsClick}
          disabled={isGpsLocating}
          aria-label="Use current location"
          style={{ zIndex: 1000 }}
          className="absolute bottom-4 left-3 flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] transition-colors shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
        >
          {isGpsLocating
            ? <Loader2 size={18} className="animate-spin" />
            : <Navigation size={18} />}
        </button>

        {/* Layer switcher — bottom-right, above GPS button */}
        <div style={{ zIndex: 1000 }} className="absolute bottom-20 right-3">
          <MapStyleSwitcher active={activeStyle} onChange={setActiveStyle} />
        </div>
      </div>

      {/* Bottom strip — coordinate readout + manual entry toggle + confirm */}
      <div className="shrink-0 px-4 pt-3 pb-4 bg-[var(--color-surface)] border-t border-[var(--color-border)] flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-body-sm text-[var(--color-text-muted)] tabular-nums">
            {center.lat.toFixed(6)}, {center.lng.toFixed(6)}
          </p>
          <button
            type="button"
            onClick={() => setShowManual((s) => !s)}
            className="text-body-xs text-[var(--color-accent)] hover:opacity-80 transition-opacity shrink-0"
          >
            {showManual ? 'Hide manual entry' : 'Enter coordinates manually'}
          </button>
        </div>

        {showManual && (
          <div className="flex gap-2">
            <div className="flex flex-col gap-1.5 flex-1">
              <label htmlFor="map-picker-lat" className="text-body-xs text-[var(--color-text-muted)]">
                Latitude
              </label>
              <input
                id="map-picker-lat"
                type="number"
                step="0.0001"
                value={center.lat}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v)) {
                    setCenter({ lat: v, lng: center.lng });
                    mapRef.current?.flyTo({ center: [center.lng, v], zoom: PINNED_ZOOM, duration: 400 });
                  }
                }}
                className="bg-[var(--color-elevated)] rounded-md text-body-md text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] px-3 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-0"
              />
            </div>
            <div className="flex flex-col gap-1.5 flex-1">
              <label htmlFor="map-picker-lng" className="text-body-xs text-[var(--color-text-muted)]">
                Longitude
              </label>
              <input
                id="map-picker-lng"
                type="number"
                step="0.0001"
                value={center.lng}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v)) {
                    setCenter({ lat: center.lat, lng: v });
                    mapRef.current?.flyTo({ center: [v, center.lat], zoom: PINNED_ZOOM, duration: 400 });
                  }
                }}
                className="bg-[var(--color-elevated)] rounded-md text-body-md text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] px-3 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-0"
              />
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={handleConfirm}
          className="w-full h-12 rounded-lg bg-ember-500 hover:bg-ember-400 active:bg-ember-600 text-white font-semibold text-body-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400"
        >
          Confirm Location
        </button>
      </div>

    </div>,
    document.body,
  );
}
